import { Banner, BlockStack, Button, InlineStack, Page, Spinner, Text } from "@shopify/polaris";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";

import { authRecovery, type RecoveryReason } from "../api/authRecovery";
import { t } from "../lib/i18n";

const MESSAGES: Record<RecoveryReason, string> = {
  unknown: "接続の結果を確認できません。新しい接続は開始せず、保存された状態を再確認してください。",
  failed: "接続を完了できませんでした。状態を再確認してから接続をやり直せます。",
  expired: "接続の有効期限が切れました。状態を再確認してから接続をやり直せます。",
  superseded: "別の接続操作に切り替わりました。現在の状態を再確認してください。",
  conflict: "別の担当者または更新処理が接続を進めています。完了後に状態を再確認してください。",
  unauthorized: "Shopifyの認証を確認できません。Shopify管理画面からアプリを開き直してください。",
  redact: "以前のデータを削除しています。完了するまで接続を開始できません。",
  unavailable: "応答を確認できませんでした。操作を繰り返さず、保存された状態を再確認してください。",
  poll_limit: "接続を処理しています。自動確認を停止しました。しばらくしてから状態を再確認してください。",
  missing: "保存された接続操作はありません。接続を開始できます。",
  context_invalid: "この画面から接続を開始できません。Shopify管理画面からアプリを開き直してください。",
  not_ready: "接続後の店舗情報を確認できません。保存された状態を再確認してください。",
  ready: "保存された接続操作を続けられます。",
  reauthorize: "保存された接続は終了しています。現在の店舗との接続を確認できます。",
};

export function AuthRecoveryBoundary({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(authRecovery.subscribe, authRecovery.getSnapshot);
  const queryClient = useQueryClient();
  const lastCompletion = useRef(state.manualCompletion);

  useEffect(() => { void authRecovery.observe(); }, []);
  useEffect(() => {
    if (state.manualCompletion === lastCompletion.current) return;
    lastCompletion.current = state.manualCompletion;
    // 手動回復後は読取りだけを再取得。mutationや課金確認を含むqueryは再実行しない。
    void queryClient.refetchQueries({ type: "active", predicate: (query) => query.meta?.authRecoveryReadOnly !== false }, { cancelRefetch: false });
  }, [queryClient, state.manualCompletion]);

  const blocked = state.phase === "blocked" || state.phase === "working";
  const hideContent = blocked && !state.accessAvailable;
  const restartAllowed = !state.busy && (state.reason === "failed" || state.reason === "expired");
  const continueAllowed = !state.busy && (state.reason === "ready" || state.reason === "missing" || state.reason === "reauthorize");
  const installationConfirmation = !state.busy && (state.reason === "unknown" || state.reason === "conflict");
  const setup = state.setupStatus !== undefined ? state.setupStatus
    : state.phase === "completed" ? state.operation?.setup_status : undefined;
  const setupNeedsAttention = ["failed", "unknown", "partial"].includes(setup ?? "");

  return <>
    {!blocked && state.offlineTokenState === "uncertain" && <Page>
      <Banner title={t("店舗の接続を回復してください")} tone="warning"
        action={{ content: t("接続を回復"), onAction: () => void authRecovery.recoverToken() }}>
        <Text as="p">{t("保存済みデータは閲覧できます。注文の取得などは、新しい接続を確認するまで停止しています。")}</Text>
      </Banner>
    </Page>}
    {blocked && <Page title="DenchoBox">
      <Banner title={t(state.busy ? "Shopifyとの接続を確認しています" : "接続状態を確認してください")} tone={state.reason === "unauthorized" || state.reason === "context_invalid" ? "warning" : "info"}>
        <BlockStack gap="300">
          {state.busy ? <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" accessibilityLabel={t("接続を確認中")} />
            <Text as="p">{t("この画面を保ったまま、店舗との接続を確認しています。")}</Text>
          </InlineStack> : <Text as="p">{t(MESSAGES[state.reason ?? "unavailable"])}</Text>}
          <InlineStack gap="200">
            <Button disabled={state.busy} onClick={() => void authRecovery.check()}>{t("状態を再確認")}</Button>
            {continueAllowed && <Button onClick={() => void authRecovery.continueOperation()}>{t(state.reason === "missing" ? "接続を開始" : "接続を続ける")}</Button>}
            {restartAllowed && <Button onClick={() => void authRecovery.restart()}>{t("接続をやり直す")}</Button>}
            {installationConfirmation && <Button onClick={() => void authRecovery.confirmInstallation()}>{t("再インストール後の接続を確認")}</Button>}
          </InlineStack>
          {installationConfirmation && <Text as="p">{t("Shopifyで再インストールを済ませた場合だけ、接続を確認できます。")}</Text>}
        </BlockStack>
      </Banner>
    </Page>}
    {!blocked && setup !== undefined && setup !== "completed" && <Page>
      <Banner title={t(setupNeedsAttention ? "注文取込の準備状況を確認してください" : "接続済み・注文取込の準備中")}
        tone={setupNeedsAttention ? "warning" : "info"} action={{ content: t("状態を再確認"), onAction: () => void authRecovery.check() }}>
        <Text as="p">{t(setup === null ? "店舗との接続は完了しました。注文取込の準備状況はまだ確認できていません。" : "店舗との接続は完了しました。注文の取込準備は別に進むため、まだ注文が表示されない場合があります。")}</Text>
      </Banner>
    </Page>}
    {/* 回復中もcomponent/mutationの寿命は保つ。認証後の副作用をunmountで失わない。 */}
    <div hidden={hideContent}>{children}</div>
  </>;
}
